import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { StripeProvider } from "@/components/payment/StripeProvider";
import { AuthModalsProvider } from "@/contexts/AuthModalsContext";
import { SearchModalProvider } from "@/contexts/SearchModalContext";
import { AuthProvider } from "@/auth/AuthProvider";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SkipToContent } from "@/components/ui/skip-to-content";
import { ContentFallback } from "@/components/ui/content-fallback";

import Protected from "./auth/Protected";

// Lazy load route components
const Index = lazy(() => import("./pages/Index"));
const Stays = lazy(() => import("./pages/Stays"));
const HotelDetails = lazy(() => import("./pages/HotelDetails"));
const HotelDetailsLuxury = lazy(() => import("./pages/HotelDetailsLuxury"));
const Reviews = lazy(() => import("./pages/Reviews"));
const DatabaseTest = lazy(() => import("./pages/DatabaseTest"));
const DumpProcessor = lazy(() => import("./pages/DumpProcessor"));

const RequestAccess = lazy(() => import("./pages/RequestAccess"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AccountLayout = lazy(() => import("./pages/account/AccountLayout"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Admin = lazy(() => import("./pages/Admin"));
const DataRestoration = lazy(() => import("./pages/DataRestoration"));
const DebugNarcissusPrices = lazy(() => import("./pages/DebugNarcissusPrices"));
const DebugHotelpageRaw = lazy(() => import("./pages/DebugHotelpageRaw"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const About = lazy(() => import("./pages/About"));
const RefundsPolicy = lazy(() => import("./pages/RefundsPolicy"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Welcome = lazy(() => import("./pages/Welcome"));

const queryClient = new QueryClient();

const AppContent = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="app-root">
      {children}
    </div>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <StripeProvider>
          <AuthProvider>
            <AuthModalsProvider>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <SearchModalProvider>
                  <SkipToContent />
                  <ScrollToTop />
                  <Toaster />
                  <Sonner />
                  <AppContent>
                    <ErrorBoundary>
                      <Routes>
                        <Route path="/" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Index />
                          </Suspense>
                        } />
                        
                        <Route path="/stays" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ErrorBoundary><Stays /></ErrorBoundary>
                          </Suspense>
                        } />
                        
                        
                        <Route path="/hotel/:hotelId" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ErrorBoundary><HotelDetails /></ErrorBoundary>
                          </Suspense>
                        } />
                        
                        <Route path="/hotel-details" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ErrorBoundary><HotelDetailsLuxury /></ErrorBoundary>
                          </Suspense>
                        } />
                        
                        <Route path="/checkout" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Protected><ErrorBoundary><Checkout /></ErrorBoundary></Protected>
                          </Suspense>
                        } />
                        
                        <Route path="/booking-confirmation" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <BookingConfirmation />
                          </Suspense>
                        } />
                        
                        <Route path="/request-access" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <RequestAccess />
                          </Suspense>
                        } />
                        
                        <Route path="/forgot-password" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ForgotPassword />
                          </Suspense>
                        } />
                        
                        <Route path="/reset-password" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <ResetPassword />
                          </Suspense>
                        } />
                        
                        <Route path="/reviews" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Reviews />
                          </Suspense>
                        } />
                        
                        <Route path="/admin" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Protected><Admin /></Protected>
                          </Suspense>
                        } />
                        
                        <Route path="/dump-processor" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Protected><DumpProcessor /></Protected>
                          </Suspense>
                        } />
                        
                        <Route path="/data-restoration" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <DataRestoration />
                          </Suspense>
                        } />
                        
                        <Route path="/account" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Protected><ErrorBoundary><AccountLayout /></ErrorBoundary></Protected>
                          </Suspense>
                        } />
                        
                        <Route path="/database-test" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <DatabaseTest />
                          </Suspense>
                        } />

                        <Route path="/debug/narcissus-prices" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <DebugNarcissusPrices />
                          </Suspense>
                        } />

                        <Route path="/debug/hotelpage-raw" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <DebugHotelpageRaw />
                          </Suspense>
                        } />

                        <Route path="/privacy-policy" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <PrivacyPolicy />
                          </Suspense>
                        } />

                        <Route path="/terms" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <TermsAndConditions />
                          </Suspense>
                        } />

                        <Route path="/about" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <About />
                          </Suspense>
                        } />

                        <Route path="/refunds-policy" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <RefundsPolicy />
                          </Suspense>
                        } />

                        <Route path="/help" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <HelpCenter />
                          </Suspense>
                        } />

                        <Route path="/cookie-policy" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <CookiePolicy />
                          </Suspense>
                        } />

                        <Route path="/welcome" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <Welcome />
                          </Suspense>
                        } />
                        
                        <Route path="*" element={
                          <Suspense fallback={<ContentFallback type="page" />}>
                            <NotFound />
                          </Suspense>
                        } />
                      </Routes>
                    </ErrorBoundary>
                  </AppContent>
                </SearchModalProvider>
              </BrowserRouter>
            </AuthModalsProvider>
          </AuthProvider>
        </StripeProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
